import { BoundingBox } from '@newroom-connect/library/types';

import { GeometryHelper, Point, CornerPositions, Rectangle } from './geometry.helper';

/**
 * Transform Helper.
 *
 * Provides utility functions for handling perspective transforms,
 * coordinate normalization, and bounding box perspective data management.
 */

export interface PerspectiveData {
  transformMatrix?: string;
  cornerPositions?: CornerPositions;
  cornerPositionsNormalized?: boolean;
  referenceSize?: { width: number; height: number };
}

export interface NormalizedCorners {
  topLeft: Point;
  topRight: Point;
  bottomLeft: Point;
  bottomRight: Point;
}

export class TransformHelper {
  /**
   * Convert corner positions to normalized percentages relative to element size.
   *
   * @param corners - Corner positions in screen coordinates.
   * @param elementRect - Element rectangle.
   * @returns Normalized corner positions (0-100).
   */
  public static normalizeCornersToElement(corners: CornerPositions, elementRect: Rectangle): NormalizedCorners {
    return {
      topLeft: {
        x: ((corners.topLeft.x - elementRect.left) / elementRect.width) * 100,
        y: ((corners.topLeft.y - elementRect.top) / elementRect.height) * 100
      },
      topRight: {
        x: ((corners.topRight.x - elementRect.left) / elementRect.width) * 100,
        y: ((corners.topRight.y - elementRect.top) / elementRect.height) * 100
      },
      bottomLeft: {
        x: ((corners.bottomLeft.x - elementRect.left) / elementRect.width) * 100,
        y: ((corners.bottomLeft.y - elementRect.top) / elementRect.height) * 100
      },
      bottomRight: {
        x: ((corners.bottomRight.x - elementRect.left) / elementRect.width) * 100,
        y: ((corners.bottomRight.y - elementRect.top) / elementRect.height) * 100
      }
    };
  }

  /**
   * Convert normalized corner positions back to screen coordinates.
   *
   * @param normalizedCorners - Normalized corner positions (0-100).
   * @param elementRect - Element rectangle.
   * @returns Corner positions in screen coordinates.
   */
  public static denormalizeCornersFromElement(normalizedCorners: NormalizedCorners, elementRect: Rectangle): CornerPositions {
    return {
      topLeft: {
        x: elementRect.left + (normalizedCorners.topLeft.x / 100) * elementRect.width,
        y: elementRect.top + (normalizedCorners.topLeft.y / 100) * elementRect.height
      },
      topRight: {
        x: elementRect.left + (normalizedCorners.topRight.x / 100) * elementRect.width,
        y: elementRect.top + (normalizedCorners.topRight.y / 100) * elementRect.height
      },
      bottomLeft: {
        x: elementRect.left + (normalizedCorners.bottomLeft.x / 100) * elementRect.width,
        y: elementRect.top + (normalizedCorners.bottomLeft.y / 100) * elementRect.height
      },
      bottomRight: {
        x: elementRect.left + (normalizedCorners.bottomRight.x / 100) * elementRect.width,
        y: elementRect.top + (normalizedCorners.bottomRight.y / 100) * elementRect.height
      }
    };
  }

  /**
   * Convert corner positions to percentages relative to parent container.
   *
   * @param corners - Corner positions in screen coordinates.
   * @param parentRect - Parent container rectangle.
   * @returns Corner positions as percentages (0-100).
   */
  public static cornersToParentPercentages(corners: CornerPositions, parentRect: Rectangle): CornerPositions {
    return {
      topLeft: {
        x: ((corners.topLeft.x - parentRect.left) / parentRect.width) * 100,
        y: ((corners.topLeft.y - parentRect.top) / parentRect.height) * 100
      },
      topRight: {
        x: ((corners.topRight.x - parentRect.left) / parentRect.width) * 100,
        y: ((corners.topRight.y - parentRect.top) / parentRect.height) * 100
      },
      bottomLeft: {
        x: ((corners.bottomLeft.x - parentRect.left) / parentRect.width) * 100,
        y: ((corners.bottomLeft.y - parentRect.top) / parentRect.height) * 100
      },
      bottomRight: {
        x: ((corners.bottomRight.x - parentRect.left) / parentRect.width) * 100,
        y: ((corners.bottomRight.y - parentRect.top) / parentRect.height) * 100
      }
    };
  }

  /**
   * Convert parent percentage corners to screen coordinates.
   *
   * @param percentageCorners - Corner positions as percentages (0-100).
   * @param parentRect - Parent container rectangle.
   * @returns Corner positions in screen coordinates.
   */
  public static parentPercentagesToCorners(percentageCorners: CornerPositions, parentRect: Rectangle): CornerPositions {
    return {
      topLeft: {
        x: parentRect.left + (percentageCorners.topLeft.x / 100) * parentRect.width,
        y: parentRect.top + (percentageCorners.topLeft.y / 100) * parentRect.height
      },
      topRight: {
        x: parentRect.left + (percentageCorners.topRight.x / 100) * parentRect.width,
        y: parentRect.top + (percentageCorners.topRight.y / 100) * parentRect.height
      },
      bottomLeft: {
        x: parentRect.left + (percentageCorners.bottomLeft.x / 100) * parentRect.width,
        y: parentRect.top + (percentageCorners.bottomLeft.y / 100) * parentRect.height
      },
      bottomRight: {
        x: parentRect.left + (percentageCorners.bottomRight.x / 100) * parentRect.width,
        y: parentRect.top + (percentageCorners.bottomRight.y / 100) * parentRect.height
      }
    };
  }

  /**
   * Update perspective data in bounding box with current transform state.
   *
   * @param boundingBox - Bounding box to update.
   * @param corners - Current corner positions.
   * @param elementRect - Element rectangle.
   * @param transformMatrix - Current CSS transform matrix.
   * @returns Updated perspective data.
   */
  public static updatePerspectiveData(
    boundingBox: BoundingBox,
    corners: CornerPositions,
    elementRect: Rectangle,
    transformMatrix: string
  ): PerspectiveData {
    const normalizedCorners = this.normalizeCornersToElement(corners, elementRect);
    const currentDimensions = { width: elementRect.width, height: elementRect.height };

    return {
      ...boundingBox.perspective,
      transformMatrix,
      cornerPositions: normalizedCorners,
      cornerPositionsNormalized: true,
      referenceSize: currentDimensions
    };
  }

  /**
   * Create a patch bounding box with only perspective updates.
   * This preserves existing spherical and layout data while updating only perspective information.
   *
   * @param boundingBox - Current bounding box.
   * @returns Patch bounding box with perspective updates.
   */
  public static createPerspectivePatch(boundingBox: BoundingBox): BoundingBox {
    return {
      width: boundingBox.width,
      height: boundingBox.height,
      top: boundingBox.top,
      left: boundingBox.left,
      perspective: {
        ...boundingBox.perspective
      }
    };
  }

  /**
   * Check if bounding box has valid perspective data.
   *
   * @param boundingBox - Bounding box to check.
   * @returns True if has valid perspective data.
   */
  public static hasPerspectiveData(boundingBox: BoundingBox): boolean {
    return !!(boundingBox.perspective?.cornerPositions);
  }

  /**
   * Restore corners from perspective data in bounding box.
   *
   * @param boundingBox - Bounding box with perspective data.
   * @param elementRect - Element rectangle.
   * @param parentRect - Parent container rectangle.
   * @returns Restored corner positions in screen coordinates.
   * @throws Error if bounding box does not contain perspective data.
   */
  public static restoreCornersFromPerspective(
    boundingBox: BoundingBox,
    elementRect: Rectangle,
    parentRect: Rectangle
  ): CornerPositions {
    if (!boundingBox.perspective || !boundingBox.perspective.cornerPositions) {
      throw new Error('Bounding box does not contain perspective data');
    }

    const corners = boundingBox.perspective.cornerPositions;
    const isNormalized = boundingBox.perspective.cornerPositionsNormalized;

    if (isNormalized) {
      // Corner positions are normalized relative to element size
      return this.denormalizeCornersFromElement(corners, elementRect);
    }

    // Corner positions are relative to parent container
    return this.parentPercentagesToCorners(corners, parentRect);
  }

  /**
   * Clear all perspective data from bounding box.
   *
   * @param boundingBox - Bounding box to clear.
   */
  public static clearPerspectiveData(boundingBox: BoundingBox): void {
    if (boundingBox.perspective) {
      delete boundingBox.perspective;
    }
  }

  /**
   * Calculate bounding box dimensions from transformed corners.
   *
   * @param corners - Transformed corner positions.
   * @param parentRect - Parent container rectangle.
   * @returns Bounding box dimensions as percentages.
   */
  public static calculateBoundingBoxFromCorners(corners: CornerPositions, parentRect: Rectangle): {
    left: number;
    top: number;
    width: number;
    height: number;
  } {
    const points = [
      corners.topLeft,
      corners.topRight,
      corners.bottomLeft,
      corners.bottomRight
    ];

    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const relativeLeft = minX - parentRect.left;
    const relativeTop = minY - parentRect.top;
    const width = maxX - minX;
    const height = maxY - minY;

    return {
      left: (relativeLeft / parentRect.width) * 100,
      top: (relativeTop / parentRect.height) * 100,
      width: (width / parentRect.width) * 100,
      height: (height / parentRect.height) * 100
    };
  }

  /**
   * Convert screen coordinates array to corner positions.
   *
   * @param corners - Array of screen coordinates.
   * @returns Corner positions object.
   * @throws Error if corners array does not contain exactly 4 points.
   */
  public static screenCoordsToCornerPositions(corners: { x: number; y: number }[]): CornerPositions {
    if (corners.length !== 4) {
      throw new Error('Corners array must contain exactly 4 points');
    }

    return {
      topLeft: corners[0],
      topRight: corners[1],
      bottomLeft: corners[2],
      bottomRight: corners[3]
    };
  }

  /**
   * Convert corner positions to screen coordinates array.
   *
   * @param corners - Corner positions object.
   * @returns Array of screen coordinates.
   */
  public static cornerPositionsToScreenCoords(corners: CornerPositions): { x: number; y: number }[] {
    return [
      corners.topLeft,
      corners.topRight,
      corners.bottomLeft,
      corners.bottomRight
    ];
  }

  /**
   * Update bounding box with transform mode data.
   *
   * @param boundingBox - Bounding box to update.
   * @param corners - Current corner positions.
   * @param parentRect - Parent container rectangle.
   * @param elementRect - Element rectangle.
   * @param transformMatrix - Current CSS transform matrix.
   * @returns Updated bounding box.
   */
  public static updateBoundingBoxWithTransformData(
    boundingBox: BoundingBox,
    corners: CornerPositions,
    parentRect: Rectangle,
    elementRect: Rectangle,
    transformMatrix: string
  ): BoundingBox {
    // Calculate bounding box from transformed corners
    const bboxData = this.calculateBoundingBoxFromCorners(corners, parentRect);

    // Update basic bounding box properties
    boundingBox.left = bboxData.left;
    boundingBox.top = bboxData.top;
    boundingBox.width = bboxData.width;
    boundingBox.height = bboxData.height;

    // Update perspective data
    boundingBox.perspective = this.updatePerspectiveData(
      boundingBox,
      corners,
      elementRect,
      transformMatrix
    );

    return boundingBox;
  }

  /**
   * Update bounding box with resize mode data.
   *
   * @param boundingBox - Bounding box to update.
   * @param elementRect - Element rectangle.
   * @param parentRect - Parent container rectangle.
   * @returns Updated bounding box.
   */
  public static updateBoundingBoxWithResizeData(
    boundingBox: BoundingBox,
    elementRect: Rectangle,
    parentRect: Rectangle
  ): BoundingBox {
    // Convert to percentage-based bounding box
    const bboxData = GeometryHelper.elementRectToPercentageBoundingBox(elementRect, parentRect);

    // Update bounding box properties
    boundingBox.left = bboxData.left;
    boundingBox.top = bboxData.top;
    boundingBox.width = bboxData.width;
    boundingBox.height = bboxData.height;

    return boundingBox;
  }
}
